import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CheckToken } from "../login/AccessToken";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import SideNavbar from "./SideNavbar";

import "./BillingPayment.scss";
import { Card } from "@mui/material";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import { usePatientContext } from "../../context/PatientContext";
import { toLegacyPatient } from "../../context/normalizePatient";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import { Radio, RadioGroup } from "@mui/material";
import InvoicePayment from "./InvoicePayment";
import ServiceAdditionOnTheGo from "./ServiceAdditionOnTheGo";
import { createOpdLabTestOrders } from "./utils/opdLabTestOrders";

import Autocomplete from "@mui/material/Autocomplete";
import { Controller } from "react-hook-form";
import { useForm } from "react-hook-form";

// import Icon from "../doctor-console/Utils/Icons";
import { ReactComponent as CalendarIcon } from "./DummyIcons/calendar-silhouette 2.svg";
import { normalizeBase64Image } from "../utils/utils";
import { displayPatientName } from "../../utils/displayPatientName";
import ApiDatePickerField from "../../components/date/ApiDatePickerField";
import ReferredByDoctorField, {
  cleanName,
  isCustomReferral,
  referralLabel,
  referralName,
  toOrderedByPayload,
  useFacilityDoctors,
} from "./ReferredByDoctorField";
import OrderedByDoctorSelect from "./OrderedByDoctorSelect";

import {
  Grid,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Avatar,
  CardContent,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Checkbox, FormControlLabel } from "@mui/material";
import { StyledTextFieldBootstrap } from "../doctor-console/StyledComponents";
import { usePermissions } from "../login/PermissionContext";

/** Booked appointment fee must match the selected doctor-charge service.
 * Appointments with no medical_service_id (legacy unpaid) stay selectable. */
function appointmentMatchesSelectedService(appointment, serviceId) {
  if (!serviceId) return true;
  const booked = appointment?.medical_service_id;
  if (booked == null || String(booked).trim() === "") return true;
  return String(booked) === String(serviceId);
}

/**
 * Doctor on the most recent line that has one — used to pre-fill other lines.
 * A consultation-fee line's locked doctor is not carried onto other lines.
 */
function lastOrderedByDoctor(rows) {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].ordered_by_doctor_id && !rows[i].ordered_by_locked)
      return rows[i].ordered_by_doctor_id;
  }
  return "";
}

const InvoiceAddition = () => {
  const today = new Date().toISOString().split("T")[0];
  const [itemsList, setItemsList] = useState([
    {
      items: "",
      quantity: "",
      price: "",
      discount: "",
      amount: "",
      category: "services",
    },
  ]);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [billDiscount, setBillDiscount] = useState(0);
  const [billDiscountAmount, setBillDiscountAmount] = useState(0);
  const billDiscountEditedBy = useRef(null);
  // Referring doctors are held by doctor_id; the `referred_by` payload is derived from names.
  const [referredByIds, setReferredByIds] = useState([]);
  // One doctor-list request shared by the Referred By field and every line's "Ordered by".
  const { doctors: facilityDoctors, loading: facilityDoctorsLoading } =
    useFacilityDoctors();
  const [showDoctorErrors, setShowDoctorErrors] = useState(false);
  const [notes, setNotes] = useState("");
  // `referredBy` is the plain names (RIS hand-off); `referredByLabel` carries the salutation and
  // is what is saved on the invoice, so the invoice view and PDF show "Dr. Sandeep Sen".
  // Consultation-fee doctors are hidden from the Referred By field but still sent with the
  // invoice, so the payload list is those doctors plus the visible Referred By entries.
  const consultationDoctorIds = [
    ...new Set(
      itemsList
        .filter((i) => i.ordered_by_locked && i.ordered_by_doctor_id)
        .map((i) => i.ordered_by_doctor_id),
    ),
  ];
  const payloadReferralIds = [
    ...new Set([...consultationDoctorIds, ...referredByIds]),
  ];
  const joinReferrals = (nameOf) =>
    payloadReferralIds
      .map((id) => cleanName(nameOf(id, facilityDoctors)))
      .filter(Boolean)
      .join(",");
  const referredBy = joinReferrals(referralName);
  const referredByLabel = joinReferrals(referralLabel);

  // Every doctor on a line is listed in Referred By; a doctor who drops off all lines is
  // removed again. Doctors added by hand that never sat on a line are left alone.
  const lineDoctorKey = JSON.stringify(
    [
      ...new Set(
        itemsList
          .filter((i) => !i.ordered_by_locked)
          .map((i) => i.ordered_by_doctor_id)
          .filter(Boolean),
      ),
    ].sort(),
  );
  const previousLineDoctors = useRef(new Set());
  useEffect(() => {
    const current = new Set(JSON.parse(lineDoctorKey));
    const previous = previousLineDoctors.current;
    previousLineDoctors.current = current;
    setReferredByIds((ids) => {
      const next = ids.filter((id) => current.has(id) || !previous.has(id));
      current.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next.length === ids.length && next.every((id, i) => id === ids[i])
        ? ids
        : next;
    });
  }, [lineDoctorKey]);

  // Once Referred By has any entry, nothing more can be added from the field itself; further
  // doctors come only from the lines' Ordered By (removing the chip re-opens the field).
  const referralLocked = referredByIds.length > 0;

  // Doctors are optional: an invoice with no ordering doctor and no Referred By goes through.
  // Once any is filled, every line needs one. A consultation fee's locked doctor is always
  // there, so it does not count.
  const doctorEngaged =
    referredByIds.length > 0 ||
    itemsList.some(
      (item) => !!item.ordered_by_doctor_id && !item.ordered_by_locked,
    );

  // Removing a referring doctor clears the lines still on them; a newly added doctor fills
  // lines that have none. Lines set to anyone else are kept.
  const handleReferredByChange = (pickedIds) => {
    let ids = pickedIds;
    // Self / External / custom names go onto the lines like a doctor (so they leave with
    // those lines).
    let added = ids.filter((id) => !referredByIds.includes(id));
    if (referralLocked && ids.some((id) => !referredByIds.includes(id))) {
      ids = ids.filter((id) => referredByIds.includes(id));
      added = [];
      toast.info("To add another doctor, select them in the item's Ordered By.");
    } else {
      // Only a first hand pick is capped to one; removing a chip keeps all the others.
      if (!referralLocked) ids = ids.slice(0, 1);
      added = added.filter((id) => ids.includes(id));
    }
    const removed = referredByIds.filter((id) => !ids.includes(id));
    setReferredByIds(ids);
    setItemsList((rows) =>
      rows.map((row) => {
        let doctorId = row.ordered_by_doctor_id || "";
        // A consultation-fee line keeps its locked doctor.
        if (
          !row.ordered_by_locked &&
          doctorId &&
          removed.some((id) => String(id) === String(doctorId))
        )
          doctorId = "";
        if (!doctorId && added.length) doctorId = added[0];
        return doctorId === (row.ordered_by_doctor_id || "")
          ? row
          : { ...row, ordered_by_doctor_id: doctorId };
      }),
    );
  };
  const [invoiceList, setInvoiceList] = useState([]);
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("");
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const [patientList, setPatientList] = useState([]);
  const [services, setServices] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [fillExpiryDate, setFillExpiryDate] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [selectedCategory, setSelectedCategory] = useState("services");
  const [servicesList, setServicesList] = useState([]);
  const [isServicesLoading, setIsServicesLoading] = useState(true);
  const [productsList, setProductsList] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [invoiceForPayment, setInvoiceForPayment] = useState(null);
  const [serviceMaster, setServiceMaster] = useState([]);
  const [patientMaster, setPatientMaster] = useState([]);
  const { userInfo } = usePermissions();

  // Appointment picker modal – opened when a "doctor appointment charge"
  // service is selected on a line item, to link the invoice to an appointment.
  const sessionDoctorId = sessionStorage.getItem("doctor_id");
  const [openAppointmentModal, setOpenAppointmentModal] = useState(false);
  const [appointmentList, setAppointmentList] = useState([]);
  const [appointmentDate, setAppointmentDate] = useState(today);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState("");
  /** Row index for which the appointment picker is open (per-line link). */
  const [appointmentPickerRowIndex, setAppointmentPickerRowIndex] =
    useState(null);
  /** Appointment IDs linked on doctor-charge lines — used after payment. */
  const linkedAppointmentIdsRef = useRef([]);
  /** Prevent double auto-dismiss (e.g. Strict Mode) for one picker open. */
  const appointmentPickerAutoDismissedRef = useRef(false);
  const [isAppointmentsLoading, setIsAppointmentsLoading] = useState(false);
  const [appointmentChargeDoctorId, setAppointmentChargeDoctorId] =
    useState("");
  /** Doctor-charge service chosen on the invoice row — picker shows only matching unpaid appointments. */
  const [appointmentPickerServiceId, setAppointmentPickerServiceId] =
    useState("");
  const [openCreateServiceModal, setOpenCreateServiceModal] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);
  const servicesFetchedForTokenRef = useRef(null);
  const appliedSnomedSelectionRef = useRef(false);
  const [serviceSearchInputByRow, setServiceSearchInputByRow] = useState({}); // index -> search string for services Autocomplete
  const searchDebounceRef = useRef(null);
  const selectedServicesCacheRef = useRef(new Map()); // id -> option, so selected services stay visible when not in search results

  const searchMedicalServices = (query, ids = null) => {
    if (!authToken) return;
    setIsServicesLoading(true);

    const isGetById = Array.isArray(ids) && ids.length > 0;

    if (isGetById) {
      // Use POST for fetching specific medical service IDs
      axios
        .post(
          `${REACT_APP_BACKEND_ROUTE_URL}get_medical_services/?episode_type=OPD`,
          { medical_service_ids: ids },
          { headers },
        )
        .then((res) => {
          const list = res.data?.data ?? res.data?.service_list ?? [];
          const filteredServices = list.filter(
            (service) => !service.expired && service.active,
          );
          setServicesList(filteredServices);
          setServiceMaster(filteredServices);
        })
        .catch((err) =>
          console.error("Error fetching medical services by ID:", err),
        )
        .finally(() => setIsServicesLoading(false));
    } else {
      // Use GET for all services, or POST for search with a query
      if (query && query.trim() !== "") {
        axios
          .post(
            `${REACT_APP_BACKEND_ROUTE_URL}get_medical_services/?episode_type=OPD`,
            { query: query },
            { headers },
          )
          .then((res) => {
            const list = res.data?.data ?? res.data?.service_list ?? [];
            const filteredServices = list.filter(
              (service) => !service.expired && service.active,
            );
            setServicesList(filteredServices);
            setServiceMaster(filteredServices);
          })
          .catch((err) =>
            console.error("Error searching medical services:", err),
          )
          .finally(() => setIsServicesLoading(false));
      } else {
        // If user hasn't typed anything, don't fetch full medical services list.
        // This prevents huge dropdown from showing on initial focus.
        setServicesList([]);
        setServiceMaster([]);
        setIsServicesLoading(false);
      }
    }
  };

  const fetchServicesList = () => {
    if (!authToken) return;
    if (medical_service_ids && medical_service_ids.length > 0) {
      searchMedicalServices("", medical_service_ids);
    } else {
      // No IDs and no query => keep empty until user types
      setServicesList([]);
      setServiceMaster([]);
      setIsServicesLoading(false);
    }
  };

  const DEBOUNCE_MS = 1500;
  const handleServiceSearchInputChange = (rowIndex, value) => {
    setServiceSearchInputByRow((prev) => ({ ...prev, [rowIndex]: value }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchMedicalServices(value);
      searchDebounceRef.current = null;
    }, DEBOUNCE_MS);
  };

  const handleServiceCreated = (service) => {
    if (!service) return;

    fetchServicesList();

    if (activeRowIndex === null || activeRowIndex === undefined) return;

    const updated = [...itemsList];
    if (!updated[activeRowIndex]) return;

    updated[activeRowIndex].items = service.medical_service_id;
    updated[activeRowIndex].price = Number(service.fee || 0).toFixed(2);
    updated[activeRowIndex].amount = calculateLineItemAmount(
      updated[activeRowIndex].quantity || 1,
      Number(service.fee || 0),
      updated[activeRowIndex].discount,
    );

    // Ensure newly created service is selectable even if we haven't fetched all services yet.
    selectedServicesCacheRef.current.set(service.medical_service_id, service);

    setItemsList(updated);
  };

  // Advanced patient search (same search types as Appointment Management)
  const [openPatientSearch, setOpenPatientSearch] = useState(false);
  const [patientSearchType, setPatientSearchType] = useState("abhaId");
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [patientSearchName, setPatientSearchName] = useState("");
  const [patientSearchGender, setPatientSearchGender] = useState("");
  const [patientSearchDob, setPatientSearchDob] = useState("");
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isPatientSearching, setIsPatientSearching] = useState(false);
  const [selectedPatientSearchId, setSelectedPatientSearchId] = useState("");

  // Care contexts (Visit Purpose) – hidden for now
  // const [careContextList, setCareContextList] = useState([]);
  // const [selectedCareContext, setSelectedCareContext] = useState("");
  // const [isCareContextsLoading, setIsCareContextsLoading] = useState(false);
  // const [showCareContextDropdown, setShowCareContextDropdown] = useState(true);
  // const isAbdmEnabled =
  //   sessionStorage.getItem("isAbdmEnabled") !== undefined &&
  //   sessionStorage.getItem("isAbdmEnabled") !== null
  //     ? sessionStorage.getItem("isAbdmEnabled")
  //     : "false";

  const formatAbhaNumber = (abhaNumber) => {
    const digits = String(abhaNumber || "").replace(/\D/g, "");
    if (digits.length !== 14) return abhaNumber || "";
    return digits.replace(/(\d{2})(\d{4})(\d{4})(\d{4})/, "$1-$2-$3-$4");
  };

  const runPatientSearch = async () => {
    if (!authToken) return;

    let filterCriteria;

    if (patientSearchType === "demographics") {
      if (
        !String(patientSearchName || "").trim() &&
        !String(patientSearchGender || "").trim() &&
        !String(patientSearchDob || "").trim()
      ) {
        toast.error("Please enter at least one demographic field.");
        return;
      }
      filterCriteria = {
        filter_type: "demographics",
        filter_details: {
          name: patientSearchName || "",
          gender: patientSearchGender || "",
          dob: patientSearchDob || "",
        },
        multipleResults: true,
      };
    } else {
      const q = String(patientSearchQuery || "").trim();
      if (!q) {
        toast.error("Please enter a search query.");
        return;
      }

      if (patientSearchType === "abhaId") {
        filterCriteria = {
          filter_type: "abha_id",
          filter_details: { abha_id: q },
          multipleResults: true,
        };
      } else if (patientSearchType === "abhaNo") {
        filterCriteria = {
          filter_type: "abha_number",
          filter_details: { abha_number: formatAbhaNumber(q) },
          multipleResults: true,
        };
      } else if (patientSearchType === "phoneNo") {
        filterCriteria = {
          filter_type: "phone_number",
          filter_details: { phone_number: q },
          multipleResults: true,
        };
      } else if (patientSearchType === "patientId") {
        filterCriteria = {
          filter_type: "patient_id",
          filter_details: { patient_id: q },
          multipleResults: true,
        };
      } else {
        toast.error("Invalid search type.");
        return;
      }
    }

    try {
      setIsPatientSearching(true);
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
        filterCriteria,
        { headers },
      );

      if (res?.data?.status === "Success") {
        const found = Array.isArray(res?.data?.patient_details)
          ? res.data.patient_details
          : [];
        setPatientSearchResults(found);
        setSelectedPatientSearchId("");
        toast.success("Patient Found Successfully");
      } else {
        setPatientSearchResults([]);
        setSelectedPatientSearchId("");
        toast.error(res?.data?.message || "No patient found");
      }
    } catch (e) {
      console.error("Patient search failed:", e);
      setPatientSearchResults([]);
      setSelectedPatientSearchId("");
      toast.error("Patient search failed");
    } finally {
      setIsPatientSearching(false);
    }
  };

  const selectPatientFromSearch = (patient) => {
    const id = patient?.patient_id;
    if (!id) return;

    // Ensure the selected patient is available to the existing Autocomplete + invoice flow
    setPatientList((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const map = new Map(list.map((p) => [p.patient_id, p]));
      map.set(id, { ...(map.get(id) || {}), ...(patient || {}) });
      return Array.from(map.values());
    });
    setPatientMaster((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const map = new Map(list.map((p) => [p.patient_id, p]));
      map.set(id, { ...(map.get(id) || {}), ...(patient || {}) });
      return Array.from(map.values());
    });

    setValue("patient_id", id);
    setPatientId(id);
    setOpenPatientSearch(false);
  };

  const { control, setValue } = useForm();

  // keep react-hook-form value in sync with selected patient
  useEffect(() => {
    setValue("patient_id", patientId || "");
  }, [patientId, setValue]);

  const handleInvoiceDateChange = (value) => {
    setInvoiceDate(value);

    if (fillExpiryDate) {
      setDueDate(value);
    }
  };
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : "",
  );
  const history = useHistory();
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  // `start_time_ist` is already IST wall-clock (often tagged with a misleading "Z").
  // Parse HH:mm from the string — do not run Date timezone conversion.
  const formatApptTime = (iso) => {
    if (!iso || typeof iso !== "string" || !iso.includes("T")) return "";
    const timeParts = iso.split("T")[1]?.split(":");
    if (!timeParts || timeParts.length < 2) return "";
    let hours = parseInt(timeParts[0], 10);
    let minutes = parseInt(timeParts[1], 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return "";
    const am_pm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes).padStart(2, "0");
    return `${hh}:${mm} ${am_pm}`;
  };

  // `start_time_ist` is IST wall-clock with a misleading "Z". Take the raw
  // date portion (YYYY-MM-DD) for date filtering — do NOT run Date timezone
  // conversion, which could shift the day. Matches `appointmentDate`'s format.
  const getApptDatePart = (iso) => {
    if (!iso || typeof iso !== "string" || !iso.includes("T")) return "";
    return iso.split("T")[0];
  };

  const getAppointmentLabel = (appointment) => {
    if (!appointment) return "";
    const name = displayPatientName(appointment?.patient_info?.name, "Patient");
    const time = formatApptTime(appointment?.start_time_ist);
    const status = appointment?.status ? ` • ${appointment.status}` : "";
    return `${time ? `${time} — ` : ""}${name}${status}`;
  };

  const fetchDoctorAppointments = (dateStr, doctorId) => {
    const idToSend = doctorId || appointmentChargeDoctorId || sessionDoctorId;
    if (!authToken || !idToSend || !dateStr) {
      setAppointmentList([]);
      setIsAppointmentsLoading(false);
      return;
    }
    setIsAppointmentsLoading(true);
    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_doctor_appointments/`,
        { id: idToSend, patient_id: patientId },
        { headers },
      )
      .then((res) => {
        if (
          res.data?.status === "Success" &&
          Array.isArray(res.data.appointments)
        ) {
          const appts = res.data.appointments
            // Invoice: only SCHEDULED + UNPAID appointments are selectable,
            // and only those booked against this consultation fee (when known).
            .filter(
              (a) =>
                String(a?.status || "").toUpperCase() === "SCHEDULED" &&
                String(a?.payment_status || "").toUpperCase() === "UNPAID" &&
                appointmentMatchesSelectedService(
                  a,
                  appointmentPickerServiceId,
                ),
            )
            .sort(
              (a, b) => new Date(a.start_time_ist) - new Date(b.start_time_ist),
            );
          setAppointmentList(appts);
        } else {
          setAppointmentList([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching doctor appointments:", err);
        setAppointmentList([]);
      })
      .finally(() => setIsAppointmentsLoading(false));
  };

  // Fetch once when the picker opens (or the doctor changes). The date is not
  // sent to the API — it only filters the already-loaded list client-side — so
  // changing appointmentDate must NOT trigger a refetch.
  useEffect(() => {
    if (openAppointmentModal) {
      fetchDoctorAppointments(appointmentDate, appointmentChargeDoctorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openAppointmentModal,
    appointmentChargeDoctorId,
    appointmentPickerServiceId,
  ]);

  // Called when a "doctor appointment charge" service is picked on a row.
  const openAppointmentPickerForService = (service, rowIndex) => {
    appointmentPickerAutoDismissedRef.current = false;
    setAppointmentPickerRowIndex(
      typeof rowIndex === "number" ? rowIndex : null,
    );
    setAppointmentChargeDoctorId(service?.doctor_id || sessionDoctorId || "");
    setAppointmentPickerServiceId(
      service?.medical_service_id ? String(service.medical_service_id) : "",
    );
    setSelectedAppointmentId("");
    setAppointmentDate(today);
    // Clear stale options so we don't auto-dismiss against a previous doctor's list.
    setAppointmentList([]);
    setIsAppointmentsLoading(true);
    setOpenAppointmentModal(true);
  };

  const getUsedAppointmentIds = (excludeRowIndex = null) => {
    const used = new Set();
    (itemsList || []).forEach((item, idx) => {
      if (excludeRowIndex != null && idx === excludeRowIndex) return;
      const id = item?.appointment_id;
      if (id != null && String(id).trim() !== "") {
        used.add(String(id));
      }
    });
    return used;
  };

  // The service search text is kept per row index, so removing a row must drop its text and
  // shift the later rows up — otherwise the next row added at that index shows the old text.
  const dropServiceSearchInput = (removedIndex) => {
    setServiceSearchInputByRow((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (i < removedIndex) next[i] = prev[key];
        else if (i > removedIndex) next[i - 1] = prev[key];
      });
      return next;
    });
  };

  /** Close picker without a selection — remove only that doctor-charge line. */
  const dismissAppointmentPickerWithoutSelection = () => {
    const rowIndex = appointmentPickerRowIndex;
    setOpenAppointmentModal(false);
    setAppointmentPickerRowIndex(null);
    setSelectedAppointmentId("");
    setAppointmentPickerServiceId("");

    if (typeof rowIndex !== "number" || rowIndex < 0) return;

    const row = itemsList[rowIndex];
    // Keep the row if an appointment was already linked (should not happen on cancel).
    if (row?.appointment_id) return;

    let next = itemsList.filter((_, i) => i !== rowIndex);
    dropServiceSearchInput(rowIndex);
    if (next.length === 0) {
      next = [
        {
          items: "",
          quantity: "",
          price: "",
          discount: "",
          amount: "",
          category: "services",
        },
      ];
    }
    setItemsList(next);
    setSubtotal(
      next.reduce((total, item) => total + parseFloat(item.amount || 0), 0),
    );
  };

  const handleAppointmentSelect = (appointment) => {
    if (!appointment) {
      setSelectedAppointmentId("");
      return;
    }

    const patientInfo = appointment.patient_info;
    const appointmentPatientId =
      patientInfo?.patient_id || appointment.patient || "";
    if (!appointmentPatientId) {
      toast.error("No patient is linked to this appointment.");
      return;
    }

    // Multi-doctor invoice: all linked appointments must belong to the same patient.
    if (patientId && String(patientId) !== String(appointmentPatientId)) {
      toast.error(
        "Selected appointment belongs to a different patient. All consultation fees on this invoice must be for the same patient.",
      );
      return;
    }

    const appointmentId = appointment.appointment_id || "";
    if (!appointmentId) return;

    if (
      !appointmentMatchesSelectedService(
        appointment,
        appointmentPickerServiceId,
      )
    ) {
      toast.error(
        "This appointment is booked against a different consultation fee.",
      );
      return;
    }

    const usedIds = getUsedAppointmentIds(appointmentPickerRowIndex);
    if (usedIds.has(String(appointmentId))) {
      toast.error(
        "This appointment is already linked to another line item on this invoice.",
      );
      return;
    }

    setSelectedAppointmentId(appointmentId);
    // Persist on the line item so multi-doctor invoices keep distinct links.
    if (
      typeof appointmentPickerRowIndex === "number" &&
      appointmentPickerRowIndex >= 0
    ) {
      setItemsList((prev) => {
        const updated = [...prev];
        if (updated[appointmentPickerRowIndex]) {
          updated[appointmentPickerRowIndex] = {
            ...updated[appointmentPickerRowIndex],
            appointment_id: appointmentId,
          };
        }
        return updated;
      });
    }

    const patientRecord = patientInfo
      ? { ...patientInfo, patient_id: appointmentPatientId }
      : { patient_id: appointmentPatientId, abha_id: appointment.abha_id };
    selectPatientFromSearch(patientRecord);
    setOpenAppointmentModal(false);
    setAppointmentPickerRowIndex(null);
    setAppointmentPickerServiceId("");
  };

  // If every unpaid appointment for this doctor/date is already linked on the
  // invoice, drop the pending doctor-charge line and close the picker.
  useEffect(() => {
    if (!openAppointmentModal || isAppointmentsLoading) return;
    if (appointmentPickerAutoDismissedRef.current) return;
    const usedIds = getUsedAppointmentIds(appointmentPickerRowIndex);
    const available = (appointmentList || []).filter(
      (a) => a?.appointment_id && !usedIds.has(String(a.appointment_id)),
    );
    if (available.length > 0) return;
    appointmentPickerAutoDismissedRef.current = true;
    toast.error(
      "No unpaid appointment for this consultation fee is available to link.",
    );
    dismissAppointmentPickerWithoutSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openAppointmentModal,
    isAppointmentsLoading,
    appointmentList,
    appointmentPickerRowIndex,
    itemsList,
  ]);

  // const fetchCareContextsByFilter = () => {
  //   if (!authToken || !patientId) {
  //     setCareContextList([]);
  //     setSelectedCareContext("");
  //     return;
  //   }
  //   const selectedPatient = patientList.find((p) => p.patient_id === patientId);
  //   if (!selectedPatient) {
  //     setCareContextList([]);
  //     setSelectedCareContext("");
  //     return;
  //   }
  //   setIsCareContextsLoading(true);
  //   setSelectedCareContext("");
  //   const postData = {};
  //   if (isAbdmEnabled === "true" && selectedPatient.abha_id) {
  //     postData.filter_type = "patient_abha";
  //     postData.filter_details = { patient_abha: selectedPatient.abha_id };
  //   } else {
  //     postData.filter_type = "patient_id";
  //     postData.filter_details = { patient_id: selectedPatient.patient_id };
  //   }
  //   axios
  //     .post(
  //       `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
  //       postData,
  //       { headers },
  //     )
  //     .then((res) => {
  //       if (res.data.status !== "Failed") {
  //         const list = res.data.care_contenxts || res.data.care_contexts || [];
  //         const arr = Array.isArray(list) ? list : [];
  //         setCareContextList(arr);
  //         if (arr.length > 0) {
  //           const first = arr[0];
  //           setSelectedCareContext(
  //             first.care_context_reference || first.care_context_id || "",
  //           );
  //         }
  //       } else {
  //         setCareContextList([]);
  //         setSelectedCareContext("");
  //       }
  //     })
  //     .catch(() => {
  //       setCareContextList([]);
  //       setSelectedCareContext("");
  //     })
  //     .finally(() => setIsCareContextsLoading(false));
  // };

  // useEffect(() => {
  //   if (!patientId) {
  //     setCareContextList([]);
  //     setSelectedCareContext("");
  //     return;
  //   }
  //   fetchCareContextsByFilter();
  // }, [patientId]);

  useEffect(() => {
    fetchInvoiceList();
  }, []);
  useEffect(() => {
    if (
      sessionStorage.getItem("accessToken") == undefined &&
      sessionStorage.getItem("accessToken") == null
    ) {
      history.push("/login");
    }
  }, []);

  const isValidDate = (value) => {
    if (!value) return false;

    const date = new Date(value);
    if (isNaN(date.getTime())) return false;

    const [y, m, d] = value.split("-").map(Number);
    return (
      date.getFullYear() === y &&
      date.getMonth() + 1 === m &&
      date.getDate() === d
    );
  };

  const handleInvoiceDateBlur = () => {
    if (!invoiceDate) return;

    if (!isValidDate(invoiceDate)) {
      toast.error("Invalid invoice date");
      setInvoiceDate("");
      return;
    }

    if (invoiceDate < today) {
      toast.error("Invoice date cannot be in the past");
      setInvoiceDate("");
    }
  };

  const handleDueDateChange = (value) => {
    setDueDate(value);
  };
  const handleDueDateBlur = () => {
    if (!dueDate) return;

    if (!isValidDate(dueDate)) {
      toast.error("Invalid due date");
      setDueDate("");
      return;
    }

    if (invoiceDate && dueDate < invoiceDate) {
      toast.error("Due date must be after invoice date");
      setDueDate("");
    }
  };

  const buildFacilityAddress = (userInfo) => {
    const parts = [
      userInfo?.facility_address_line_1,
      userInfo?.facility_address_line_2,
      userInfo?.facility_address_line_3,
      userInfo?.city,
      userInfo?.state,
      userInfo?.pincode ? `PIN: ${userInfo.pincode}` : null,
    ];

    return parts.filter((x) => x && String(x).trim() !== "").join(", ");
  };

  const [facilityName, setFacilityName] = useState(
    sessionStorage.getItem("facility_name") || "",
  );
  const location = useLocation();
  const { patient: activePatient } = usePatientContext();
  const {
    selectedDoctors,
    selectedServiceRequests,
    medical_service_ids,
    source,
    preselectedInvoicePatient: preselectedInvoicePatientFromState,
    selectedPatientId: stagingAreaPatientId,
    careContextId: stagingAreaCareContextId,
  } = location.state || {};
  // Fall back to the app-wide active patient so a new invoice keeps the selected
  // patient even when reached without navigation state (e.g. back-navigation).
  const preselectedInvoicePatient =
    preselectedInvoicePatientFromState ||
    (activePatient?.patient_id ? toLegacyPatient(activePatient) : null);
  const preselectedPatientIdFromState =
    preselectedInvoicePatient?.patient_id ||
    preselectedInvoicePatient?.pat_id ||
    stagingAreaPatientId ||
    "";
  const appliedPreselectedPatientRef = useRef(false);

  console.log(
    "test..",
    selectedDoctors,
    selectedServiceRequests,
    stagingAreaPatientId,
    medical_service_ids,
    source,
  );

  // useEffect(() => {
  //   const maxInvoice = getMaxInvoiceNumber(invoiceList);
  //   // nextInvoiceNumber(maxInvoice);
  // }, [invoiceList]);

  // useEffect(() => {
  //   const maxInvoice = getMaxInvoiceNumber(invoiceList);
  //   setNextInvoiceNumber(maxInvoice);
  // }, [invoiceList]);

  // const getMaxInvoiceNumber = (invoiceList) => {
  //   if (invoiceList.length === 0) {
  //     return null;
  //   }

  //   const maxInvoiceNumber = invoiceList.reduce((max, invoice) => {
  //     const currentNumber = parseInt(invoice.invoice_number.slice(3), 10);
  //     return currentNumber > max ? currentNumber : max;
  //   }, 0);

  //   const nextInvoiceNumber = maxInvoiceNumber + 1;
  //   console.log("he", nextInvoiceNumber)
  //   return `INV${String(nextInvoiceNumber).padStart(4, '0')}`;
  // };

  // const getMaxInvoiceNumber = (invoiceList) => {
  //   if (invoiceList.length === 0) {
  //     return "INV0001"; // Default invoice number when there are no invoices
  //   }

  //   const maxInvoiceNumber = invoiceList.reduce((max, invoice) => {
  //     const currentNumber = parseInt(invoice.invoice_number.slice(3), 10);
  //     return currentNumber > max ? currentNumber : max;
  //   }, 0);

  //   const nextInvoiceNumber = maxInvoiceNumber + 1;

  //   return `INV${String(nextInvoiceNumber).padStart(4, '0')}`;
  // };

  useEffect(() => {
    if (authToken === "") {
      setIsServicesLoading(false);
      return;
    }

    // Build a key that includes both authToken and medical_service_ids
    const idsKey = medical_service_ids ? medical_service_ids.join(",") : "";
    const fetchKey = `${authToken}_${idsKey}`;

    // Prevent duplicate API call for the same token+IDs
    if (servicesFetchedForTokenRef.current === fetchKey) return;
    servicesFetchedForTokenRef.current = fetchKey;

    // Fetch services - use specific IDs if available, otherwise fetch all
    if (medical_service_ids && medical_service_ids.length > 0) {
      searchMedicalServices("", medical_service_ids);
    } else {
      // No IDs => keep dropdown empty until user types
      setServicesList([]);
      setServiceMaster([]);
      setIsServicesLoading(false);
    }

    // Fetch products
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_all_medical_products/`, {
        headers,
      })
      .then((res) => {
        const filteredProducts = res.data.product_list.filter(
          (product) => !product.expired && product.active,
        );
        setProductsList(filteredProducts);
      });
  }, [authToken, medical_service_ids]);

  const fetchInvoiceList = () => {
    if (!authToken) return;

    (async () => {
      try {
        // Preferred: ask backend for full list in one go.
        const res = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}get_all_invoices/`,
          { hip_id: facilityName, is_pagination_required: false },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );

        const pg = res?.data?.pagination;
        const invoices = Array.isArray(res?.data?.invoices)
          ? res.data.invoices
          : [];

        // If backend still paginates even when flag=false, fall back to paging all.
        const backendIgnoredFlag = Boolean(pg?.has_next);
        if (backendIgnoredFlag) {
          const LIMIT = 100; // backend caps limit to 100
          const MAX_PAGES_SAFETY = 1000;
          let page = 1;
          let allInvoices = [];
          let pagesFetched = 0;

          while (pagesFetched < MAX_PAGES_SAFETY) {
            const r = await axios.post(
              `${REACT_APP_BACKEND_ROUTE_URL}get_all_invoices/`,
              {
                hip_id: facilityName,
                is_pagination_required: true,
                page,
                limit: LIMIT,
              },
              { headers: { Authorization: `Bearer ${authToken}` } },
            );

            const pageInvoices = Array.isArray(r?.data?.invoices)
              ? r.data.invoices
              : [];
            allInvoices = allInvoices.concat(pageInvoices);

            const p = r?.data?.pagination;
            if (!p || !p.has_next) break;

            const next = p.next_page;
            if (typeof next === "number" && next > page) {
              page = next;
            } else {
              page = page + 1;
            }

            pagesFetched += 1;
          }

          const invoicesWithId = allInvoices.map((invoice) => ({
            ...invoice,
            id: invoice.invoice_id,
            total: parseFloat(invoice.total_amount).toFixed(2),
          }));
          setInvoiceList(invoicesWithId);
          return;
        }

        const invoicesWithId = invoices.map((invoice) => ({
          ...invoice,
          id: invoice.invoice_id,
          total: parseFloat(invoice.total_amount).toFixed(2),
        }));
        setInvoiceList(invoicesWithId);
      } catch (error) {
        console.error("Error fetching invoice list:", error);
      }
    })();
  };
  // console.log("firstpalce",invoiceList)
  const Token = () => {
    const check = CheckToken();
    console.log(check);
    setAuthToken(sessionStorage.getItem("accessToken"));
    return check;
  };
  useEffect(() => {
    calculateTotalAmount(); // Recalculate total when subtotal or tax changes
  }, [subtotal, tax]);

  // useEffect(() => {
  //   if (authToken !== '') {
  //     axios
  //       .get(`${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`, {
  //         headers: headers,
  //       })
  //       .then((res) => {
  //         setPatientList(res.data);
  //       });

  //   }
  // }, [authToken]);
  // console.log("testing..", services)
  useEffect(() => {
    if (authToken !== "") {
      axios
        .get(
          `${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`,
          {
            headers: headers,
          },
        )
        .then((res) => {
          const patients = res.data?.data || [];
          let mergedPatients = patients;

          // If we arrived here with a preselected patient (from Invoice list),
          // make sure that patient is present in the autocomplete/list data.
          if (
            preselectedPatientIdFromState &&
            !appliedPreselectedPatientRef.current
          ) {
            const existing = patients.find(
              (p) => p.patient_id === preselectedPatientIdFromState,
            );
            if (!existing && preselectedInvoicePatient) {
              mergedPatients = [
                {
                  ...preselectedInvoicePatient,
                  patient_id: preselectedPatientIdFromState,
                },
                ...patients,
              ];
            }
          }

          setPatientList(mergedPatients);
          setPatientMaster(mergedPatients);

          if (
            preselectedPatientIdFromState &&
            !appliedPreselectedPatientRef.current
          ) {
            appliedPreselectedPatientRef.current = true;
            setPatientId(preselectedPatientIdFromState);
          }
        });
    }
  }, [authToken, preselectedPatientIdFromState]);
  console.log("patientId", patientId);

  // useEffect(() => {
  //   if (authToken !== '') {
  //     axios
  //       .get(`${REACT_APP_BACKEND_ROUTE_URL}get_all_medical_services/`, {
  //         headers: headers,
  //       })
  //       .then((res) => {
  //         const filteredServices = res.data.service_list.filter(service => !service.expired && service.active);

  //         // Set the filtered services
  //         setServices(filteredServices);

  //         // Filter and add matching services to itemsList
  //         if (selectedServiceRequests && selectedServiceRequests.length > 0) {
  //           const matchingItems = filteredServices.filter(service => snomedCodes.includes(service.snomed_code));
  //           const initialItemsList = matchingItems.map(item => ({
  //             items: item.medical_service_id,
  //             quantity: '',
  //             price: item.fee.toFixed(2),
  //             amount: '',
  //           }));
  //           setItemsList(initialItemsList);
  //         }
  //       });
  //   }
  // }, [authToken, selectedServiceRequests]);
  // Use servicesList from the single API call above; no second fetch
  useEffect(() => {
    if (
      !authToken ||
      servicesList.length === 0 ||
      !selectedServiceRequests?.length
    )
      return;
    if (appliedSnomedSelectionRef.current) return;

    setServices(servicesList);

    const requestedIds = (medical_service_ids || []).map((id) => String(id));
    const byServiceId = new Map(
      servicesList.map((s) => [String(s.medical_service_id), s]),
    );
    const matchingItems =
      requestedIds.length > 0
        ? requestedIds.map((id) => byServiceId.get(id)).filter(Boolean)
        : [];

    if (matchingItems.length > 0) {
      appliedSnomedSelectionRef.current = true;
      const initialItemsList = matchingItems.map((item, idx) => {
        const amount = (item.fee * 1).toFixed(2);

        // Keep selected items visible even if user searches and current
        // servicesList no longer contains the chosen value.
        selectedServicesCacheRef.current.set(item.medical_service_id, item);

        // Sync with react-hook-form state so Autocomplete shows the name
        setValue(`items.${idx}.item_id`, item.medical_service_id);
        setValue(`items.${idx}.category`, "services");

        return {
          category: "services",
          items: item.medical_service_id,
          quantity: "1",
          price: item.fee.toFixed(2),
          discount: "",
          amount: amount,
        };
      });
      setItemsList(initialItemsList);
    }
  }, [
    authToken,
    selectedServiceRequests,
    servicesList,
    medical_service_ids,
    setValue,
  ]);

  const calculateSubtotal = () => {
    const calculatedSubtotal = itemsList.reduce(
      (total, item) => total + parseFloat(item.amount || 0),
      0,
    );
    setSubtotal(calculatedSubtotal);
  };

  const getItemsSubtotal = (list = itemsList) =>
    list.reduce((total, item) => total + parseFloat(item.amount || 0), 0);

  const getLineDiscountTotal = (list = itemsList) =>
    list.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const gross = qty * price;
      const net = parseFloat(item.amount) || 0;
      return sum + Math.max(gross - net, 0);
    }, 0);

  const getTotalDiscountAmount = () => {
    const lineDisc = getLineDiscountTotal();
    const billDisc = Math.max(Number(billDiscountAmount) || 0, 0);
    return Number((lineDisc + billDisc).toFixed(2));
  };

  const calculateTotalAmount = () => {
    const itemsTotal = getItemsSubtotal();
    const discAmt = Math.min(
      Math.max(Number(billDiscountAmount) || 0, 0),
      itemsTotal,
    );
    const afterBillDiscount = Math.max(itemsTotal - discAmt, 0);
    const taxAmount = afterBillDiscount * (tax / 100);
    return (afterBillDiscount + taxAmount).toFixed(2);
  };

  const syncBillDiscountFromPercent = (pct, itemsTotal) => {
    billDiscountEditedBy.current = "percent";
    let percent = Number(pct);
    if (!Number.isFinite(percent) || percent < 0) percent = 0;
    let capped = false;
    if (percent > 100) {
      percent = 100;
      capped = true;
    }
    const base = Number(itemsTotal) || 0;
    const amount = Number(((base * percent) / 100).toFixed(2));
    setBillDiscount(capped ? percent : pct);
    setBillDiscountAmount(Math.min(amount, Number(base.toFixed(2))));
  };

  const syncBillDiscountFromAmount = (amt, itemsTotal) => {
    billDiscountEditedBy.current = "amount";
    const base = Number(itemsTotal) || 0;
    let amount = Number(amt);
    if (!Number.isFinite(amount) || amount < 0) amount = 0;
    let capped = false;
    if (amount > base) {
      amount = Number(base.toFixed(2));
      capped = true;
    }
    const percent = base > 0 ? (amount / base) * 100 : 0;
    setBillDiscountAmount(capped ? amount : amt);
    setBillDiscount(Number(percent.toFixed(2)));
    if (capped) {
      toast.error("Bill discount amount cannot exceed items subtotal.");
    }
  };

  // useEffect(() => {
  //   if (authToken !== "") {
  //     const url =
  //       selectedCategory === "services"
  //         ? `${REACT_APP_BACKEND_ROUTE_URL}get_all_medical_services/`
  //         : `${REACT_APP_BACKEND_ROUTE_URL}get_all_medical_products/`;

  //     axios
  //       .get(url, { headers: headers })
  //       .then((res) => {
  //         const filteredItems =
  //           selectedCategory === "services"
  //             ? res.data.service_list.filter(
  //                 (item) => item.active && !item.expired
  //               )
  //             : res.data.product_list.filter(
  //                 (item) => item.active && !item.expired
  //               );

  //         setServices(filteredItems);
  //       })
  //       .catch((error) => {
  //         console.error(
  //           `Error fetching ${
  //             selectedCategory === "services" ? "services" : "products"
  //           }:`,
  //           error
  //         );
  //       });
  //   }
  // }, [selectedCategory]);

  // Outsourced services (service master `outsource_vendor` = true) bill at the service fee
  // by default; if the receptionist picks a vendor, that vendor's rate is billed instead.
  // These fields live on the line-item row.
  const resetOutsourceVendor = (row) => {
    row.is_outsourced = false;
    row.master_fee = "";
    row.vendors = [];
    row.vendor_id = "";
    row.vendorsLoading = false;
    row.vendorLookupFor = undefined;
  };

  // Runs for every way a service lands on a row (dropdown, prefilled lab requests, newly
  // created service): if it is outsourced, keep its fee as the default and load its vendors.
  useEffect(() => {
    itemsList.forEach((row) => {
      if (row.category && row.category !== "services") return;
      if (!row.items || row.vendorLookupFor === row.items) return;
      const service =
        (servicesList || []).find((s) => s.medical_service_id === row.items) ||
        (serviceMaster || []).find((s) => s.medical_service_id === row.items) ||
        selectedServicesCacheRef.current.get(row.items);
      if (!service) return;

      const serviceId = row.items;
      row.vendorLookupFor = serviceId;
      if (!service.outsource_vendor) return;

      row.is_outsourced = true;
      row.master_fee = Number(service.fee || 0).toFixed(2);
      row.vendors = [];
      row.vendor_id = "";
      row.vendorsLoading = true;
      setItemsList((list) => [...list]);

      const finish = (vendors) =>
        setItemsList((list) =>
          list.map((r) =>
            r.items === serviceId && r.vendorLookupFor === serviceId
              ? { ...r, vendors, vendorsLoading: false }
              : r,
          ),
        );
      axios
        .get(`${REACT_APP_BACKEND_ROUTE_URL}opd/outsource-vendors/`, {
          params: { medical_service_id: serviceId },
          headers,
        })
        .then((res) => {
          if (res.data?.status !== "Success") {
            toast.error(
              res.data?.message || "Could not load vendors for this service.",
            );
            finish([]);
            return;
          }
          finish(res.data.vendors || []);
        })
        .catch((err) => {
          console.error("Error fetching outsource vendors:", err);
          toast.error("Could not load vendors for this service.");
          finish([]);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsList, servicesList, serviceMaster]);

  const handleAddItem = () => {
    setItemsList([
      ...itemsList,
      {
        items: "",
        quantity: "",
        price: "",
        discount: "",
        amount: "",
        category: "services",
        ordered_by_doctor_id: lastOrderedByDoctor(itemsList),
      }, // Default category as "services"
    ]);
  };

  const calculateLineItemAmount = (quantity, price, discountPercent) => {
    const qty = parseFloat(quantity) || 0;
    const unitPrice = parseFloat(price) || 0;
    let discountValue = parseFloat(discountPercent);
    if (isNaN(discountValue)) discountValue = 0;
    if (discountValue < 0) discountValue = 0;
    if (discountValue > 100) discountValue = 100;

    const baseAmount = qty * unitPrice;
    const discountAmount = (baseAmount * discountValue) / 100;
    return (baseAmount - discountAmount).toFixed(2);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItemsList = [...itemsList];
    updatedItemsList[index][field] = value;

    if (field === "category") {
      updatedItemsList[index].category = value;
      updatedItemsList[index].items = ""; // Clear selected item
      updatedItemsList[index].price = "";
      updatedItemsList[index].discount = "";
      updatedItemsList[index].amount = "";
      updatedItemsList[index].is_doctor_appointment_charge = false;
      updatedItemsList[index].appointment_id = undefined;
      updatedItemsList[index].ordered_by_locked = false;
      resetOutsourceVendor(updatedItemsList[index]);
    } else if (field === "vendor_id") {
      const vendor = (updatedItemsList[index].vendors || []).find(
        (v) => String(v.vendor_id) === String(value),
      );
      updatedItemsList[index].vendor_id = value;
      // Vendor picked -> vendor's rate; cleared -> back to the service master fee.
      updatedItemsList[index].price = vendor
        ? Number(vendor.rate).toFixed(2)
        : updatedItemsList[index].master_fee || "";
      updatedItemsList[index].amount = calculateLineItemAmount(
        updatedItemsList[index].quantity,
        updatedItemsList[index].price,
        updatedItemsList[index].discount,
      );
    } else if (field === "items") {
      resetOutsourceVendor(updatedItemsList[index]);
      const selectedItem =
        updatedItemsList[index].category === "services"
          ? (servicesList || []).find((s) => s.medical_service_id === value) ||
            (serviceMaster || []).find((s) => s.medical_service_id === value) ||
            selectedServicesCacheRef.current.get(value)
          : (productsList || []).find((p) => p.product_id === value);
      if (selectedItem) {
        const quantityValue =
          updatedItemsList[index].quantity === "" ||
          updatedItemsList[index].quantity == null
            ? "1"
            : updatedItemsList[index].quantity;
        updatedItemsList[index].quantity = quantityValue;
        updatedItemsList[index].price = selectedItem.fee.toFixed(2);
        updatedItemsList[index].amount = calculateLineItemAmount(
          updatedItemsList[index].quantity,
          selectedItem.fee,
          updatedItemsList[index].discount,
        );

        // If this service is a doctor appointment charge, prompt the user to
        // link it to an appointment (which auto-fills the patient). Tag the row
        // so its appointment_id can be sent on invoice create (enables refund
        // on cancellation).
        updatedItemsList[index].is_doctor_appointment_charge =
          !!selectedItem.is_doctor_appointment_charge;
        updatedItemsList[index].appointment_id = undefined;
        updatedItemsList[index].ordered_by_locked = false;
        if (!updatedItemsList[index].ordered_by_doctor_id) {
          updatedItemsList[index].ordered_by_doctor_id =
            lastOrderedByDoctor(updatedItemsList);
        }
        if (selectedItem.is_doctor_appointment_charge) {
          // A consultation-fee line is ordered by the doctor whose fee it is, and stays locked.
          if (selectedItem.doctor_id) {
            updatedItemsList[index].ordered_by_doctor_id = selectedItem.doctor_id;
            updatedItemsList[index].ordered_by_locked = true;
          }
          openAppointmentPickerForService(selectedItem, index);
        }
      } else {
        updatedItemsList[index].quantity = "";
        updatedItemsList[index].price = "";
        updatedItemsList[index].amount = "";
        updatedItemsList[index].is_doctor_appointment_charge = false;
        updatedItemsList[index].appointment_id = undefined;
        updatedItemsList[index].ordered_by_locked = false;
      }
    } else if (field === "quantity") {
      updatedItemsList[index].amount = calculateLineItemAmount(
        value,
        updatedItemsList[index].price,
        updatedItemsList[index].discount,
      );
    } else if (field === "discount") {
      if (value === "") {
        updatedItemsList[index].discount = "";
        updatedItemsList[index].amount = calculateLineItemAmount(
          updatedItemsList[index].quantity,
          updatedItemsList[index].price,
          0,
        );
        setItemsList(updatedItemsList);
        calculateSubtotal();
        return;
      }
      let numericDiscount = parseFloat(value);
      if (isNaN(numericDiscount)) numericDiscount = 0;
      if (numericDiscount < 0) numericDiscount = 0;
      if (numericDiscount > 100) numericDiscount = 100;
      updatedItemsList[index].discount = String(numericDiscount);
      updatedItemsList[index].amount = calculateLineItemAmount(
        updatedItemsList[index].quantity,
        updatedItemsList[index].price,
        numericDiscount,
      );
    }

    setItemsList(updatedItemsList);
    calculateSubtotal();
  };

  const handleTotalChange = (field, value) => {
    const itemsTotal = getItemsSubtotal();

    if (field === "billDiscount") {
      if (value === "" || value == null) {
        billDiscountEditedBy.current = "percent";
        setBillDiscount("");
        setBillDiscountAmount(0);
        return;
      }
      syncBillDiscountFromPercent(value, itemsTotal);
      return;
    }

    if (field === "billDiscountAmount") {
      if (value === "" || value == null) {
        billDiscountEditedBy.current = "amount";
        setBillDiscountAmount("");
        setBillDiscount(0);
        return;
      }
      syncBillDiscountFromAmount(value, itemsTotal);
      return;
    }

    let num = parseFloat(value);
    if (isNaN(num)) num = 0;
    if (field === "tax") {
      if (num > 100) num = 100;
      if (num < 0) num = 0;
    }

    if (field === "subtotal") {
      setSubtotal(num);
    } else if (field === "tax") {
      setTax(num);
    }
  };

  useEffect(() => {
    const itemsTotal = getItemsSubtotal();
    const maxAmount = Number(Math.max(0, itemsTotal).toFixed(2));

    if (billDiscountEditedBy.current === "amount") {
      const current = Number(billDiscountAmount) || 0;
      if (current > maxAmount + 0.001) {
        setBillDiscountAmount(maxAmount);
        setBillDiscount(
          itemsTotal > 0
            ? Number(((maxAmount / itemsTotal) * 100).toFixed(2))
            : 0,
        );
      } else if (itemsTotal > 0) {
        setBillDiscount(Number(((current / itemsTotal) * 100).toFixed(2)));
      }
      return;
    }

    let pct = Number(billDiscount);
    if (!Number.isFinite(pct) || pct < 0) pct = 0;
    if (pct > 100) pct = 100;
    const derived = Math.min(
      Number(((itemsTotal * pct) / 100).toFixed(2)),
      maxAmount,
    );
    setBillDiscountAmount((prev) => {
      if (Math.abs(derived - (Number(prev) || 0)) <= 0.001) return prev;
      return derived;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsList]);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!patientId) {
      toast.error("Please select a patient.");
      return;
    }

    const filteredItems = itemsList.filter(
      (item) =>
        item.items &&
        item.quantity != null &&
        item.quantity != null &&
        item.quantity !== "",
    );

    // Check if any required item or quantity is not selected
    if (filteredItems.length !== itemsList.length) {
      console.log(filteredItems.length);
      console.log(itemsList.length);
      toast.error("Please select an item and enter the quantity for all rows.");
      return;
    }

    if (doctorEngaged && itemsList.some((item) => !item.ordered_by_doctor_id)) {
      setShowDoctorErrors(true);
      toast.error("Please select the ordering doctor for every item.");
      return;
    }

    //const tot = document.getElementById("totalAmount").value;
    const tot = calculateTotalAmount();
    // Validate total_amount
    if (isNaN(tot) || tot <= 0) {
      toast.error("Total amount must be a valid number greater than 0.");
      return;
    }

    //const invoiceDate = document.getElementById("invoiceDate").value;
    //const dueDate = document.getElementById("dueDate").value;
    // const invoiceNumber = document.getElementById('invoiceNumber').value;
    //const patientId = document.getElementById("patientSelect").value;

    //const total = document.getElementById("totalAmount").value;
    const total = tot;

    // Perform due date validation
    if (dueDate < invoiceDate) {
      toast.error("Due date must be after the invoice date.");
      return;
    }

    const itemsListWithItemNames = itemsList.map((item) => {
      const combinedList = [...servicesList, ...productsList];

      const selectedService = combinedList.find(
        (service) => service.medical_service_id === item.items,
      );
      return {
        ...item,
        itemName: selectedService ? selectedService.name : "",
      };
    });

    // Create the request body for creating an invoice
    const tot_Amount = calculateTotalAmount();
    const tot_AmountNumber = parseFloat(tot_Amount);
    // console.log("partial_payment", partialPaymentNumber);
    console.log("totalAmount", tot_AmountNumber);
    const taxNumber = Number(tax);
    const facilityAddress = buildFacilityAddress(userInfo);

    if (taxNumber > 100) {
      toast.error("Tax cannot be more than 100%");
      return;
    }

    const hasInvalidItemDiscount = itemsList.some((item) => {
      const rawValue = item?.discount;
      if (rawValue === "" || rawValue == null) return false;
      const n = Number(rawValue);
      return !Number.isFinite(n) || n < 0 || n > 100;
    });
    if (hasInvalidItemDiscount) {
      toast.error("Each line item discount must be between 0 and 100.");
      return;
    }

    const itemsTotalForBill = getItemsSubtotal();
    const billPct = Number(billDiscount || 0);
    if (!Number.isFinite(billPct) || billPct < 0 || billPct > 100) {
      toast.error("Bill discount must be between 0 and 100.");
      return;
    }
    if (Number(billDiscountAmount || 0) > itemsTotalForBill + 0.001) {
      toast.error("Bill discount amount cannot exceed items subtotal.");
      return;
    }
    const billDiscountForApi =
      itemsTotalForBill > 0
        ? Number(
            (
              (Number(billDiscountAmount || 0) / itemsTotalForBill) *
              100
            ).toFixed(6),
          )
        : 0;

    const requestBody = {
      hip_id: facilityName,
      invoice_date: invoiceDate,
      // invoice_number: invoiceNumber,
      due_date: dueDate,
      patient_id: patientId,
      // ...(selectedCareContext && { care_context_id: selectedCareContext }),
      note: notes,
      referred_by: String(referredByLabel || "").trim(),
      total_amount: tot_Amount,
      discount: billDiscountForApi,
      tax: taxNumber,
      gst_number: "your_gst_number", // Add your GST number here
      // partial_payment: partialPayment,
      items: itemsList.map((item) => ({
        item_id: item.items,
        quantity: parseInt(item.quantity, 10),
        discount_percentage: Number(item.discount || 0),
        unit_price: Number(item.price || 0),
        amount: Number(item.price || 0),
        description: item.itemName || "",
        ...(item.ordered_by_doctor_id
          ? {
              ordered_by_doctor_id: toOrderedByPayload(
                item.ordered_by_doctor_id,
              ),
            }
          : {}),
        ...(item.is_outsourced && item.vendor_id
          ? { vendor_id: Number(item.vendor_id) }
          : {}),
        // Link each appointment-charge line to its own appointment so the
        // backend can auto-raise a refund if that paid appointment is later
        // cancelled — and so multi-doctor invoices mark each as PAID.
        ...(item.is_doctor_appointment_charge && item.appointment_id
          ? { appointment_id: item.appointment_id }
          : {}),
      })),
      // Staging Area tests billed here; the backend ties them to this invoice
      // so paying it marks them PAID.
      ...(selectedServiceRequests?.length
        ? { lab_test_ids: selectedServiceRequests }
        : {}),
    };
    console.log("requestBody", requestBody);
    const selectedPatient = patientList.find(
      (patient) => patient.patient_id === patientId,
    );
    if (!selectedPatient) {
      console.error(
        "Patient details not found for selected patient_id:",
        patientId,
      );
      return;
    }
    const patientName = selectedPatient.name;

    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}create_invoice/`,
        JSON.stringify(requestBody),
        {
          headers: headers,
        },
      )
      .then((response) => {
        if (
          response.data.status === "Success" &&
          response.data.message === "New invoice created successfully!"
        ) {
          toast.success(response.data.message);
          console.log("Invoice submitted successfully:", response.data);
          const invoiceData = {
            itemsList: itemsListWithItemNames,
            subtotal,
            tax,
            notes,
            invoiceDate,
            dueDate,
            invoiceNumber: response.data.invoice_number,
            // partialPayment,
            total_amount: total,
            discount: billDiscountForApi,
            patientName: patientName,
            patient_id: selectedPatient?.patient_id,
            age: selectedPatient?.age,
            gender: selectedPatient?.gender,
            phone_no: selectedPatient?.phone_no,
            address: selectedPatient?.address,
            abha_id: selectedPatient?.abha_id,
            referred_by: String(referredByLabel || "").trim(),
            // ...(selectedCareContext && {
            //   care_context_id: selectedCareContext,
            // }),
            // Visit of the Staging Area tests, for the RIS/LIS hand-off after
            // payment — only while the bill is still for that patient.
            ...(stagingAreaCareContextId &&
              patientId === stagingAreaPatientId && {
                care_context_id: stagingAreaCareContextId,
              }),

            // facility data
            facility_name: userInfo?.facility_name || "",
            facility_address: facilityAddress || "",
            center: userInfo?.facility_name || "N/A",
            center_address: facilityAddress || "",
          };
          console.log("invoiceData", invoiceData);

          linkedAppointmentIdsRef.current = [
            ...new Set(
              itemsList
                .filter(
                  (item) =>
                    item?.is_doctor_appointment_charge && item?.appointment_id,
                )
                .map((item) => String(item.appointment_id).trim())
                .filter(Boolean),
            ),
          ];

          // Laboratory / Radiology lines picked by hand get their lab order
          // now (NEW, tied to this invoice); Staging Area tests already have
          // one. Payment opens only once they exist, so paying straight away
          // still finds them.
          const stagedServiceIds = new Set(
            (medical_service_ids || []).map(String),
          );
          createOpdLabTestOrders({
            patientId: selectedPatient.patient_id,
            services: itemsList
              .filter(
                (item) =>
                  item.category === "services" &&
                  !stagedServiceIds.has(String(item.items)),
              )
              .map(
                (item) =>
                  (servicesList || []).find(
                    (s) => s.medical_service_id === item.items,
                  ) ||
                  (serviceMaster || []).find(
                    (s) => s.medical_service_id === item.items,
                  ) ||
                  selectedServicesCacheRef.current.get(item.items),
              )
              .filter(Boolean),
            invoiceNumber: response.data.invoice_number,
            doctorName: String(referredBy || "").trim(),
            headers,
          }).then(() => {
            setInvoiceForPayment({
              ...invoiceData,
              serviceMaster: serviceMaster,
              patientMaster: patientMaster,
            });
            setOpenPaymentModal(true);
          });
        } else {
          console.error("Invoice submission failed:", response.data.message);
          const failure = response.data?.error || response.data?.message;
          if (failure) toast.error(failure);
          // Handle other cases if needed
        }
      })
      .catch((error) => {
        console.error("Error submitting invoice:", error);
        // Handle errors (e.g., show error message to the user)
      });
  };
  const handleRemoveItem = (indexToRemove) => {
    const updatedItemsList = itemsList.filter(
      (item, index) => index !== indexToRemove,
    );
    dropServiceSearchInput(indexToRemove);
    setItemsList(updatedItemsList);
    calculateSubtotal();
  };

  const handleNotesChange = (value) => {
    setNotes(value);
  };
  const handleGoBack = () => {
    history.push("/doctor/invoice");
  };

  const handleCheckboxChange = (event) => {
    const { checked } = event.target;
    setFillExpiryDate(checked);

    if (checked) {
      setDueDate(invoiceDate);
    }
  };

  const itemFilterOptions = (options, { inputValue }) => {
    const search = inputValue.toLowerCase().trim();

    if (!search) return options;

    return options.filter(
      (option) => option.name && option.name.toLowerCase().startsWith(search),
    );
  };

  return (
    <>
      <SideNavbar>
        <div className="invoice-form-wrapper">
          <form onSubmit={handleSubmit}>
            <Card className="invoice-form-card" sx={{ boxShadow: "none" }}>
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: "32px",
                  color: "#172935",
                  marginBottom: "16px",
                }}
                className="invoice-form-title"
              >
                Add New Invoice
              </p>
              <Grid container spacing={4} style={{ alignItems: "flex-start" }}>
                {/* Patient Field (Left Side) */}
                <Grid item xs={12} sm={6} md={6}>
                  <label htmlFor="patient" className="required-label">
                    Patient{" "}
                  </label>
                  {(() => {
                    const selectedPatient =
                      patientList.find((p) => p.patient_id === patientId) ||
                      null;

                    if (!selectedPatient) {
                      return (
                        <>
                          <Card
                            onClick={() => setOpenPatientSearch(true)}
                            sx={{
                              borderRadius: "10px",
                              border: "1px dashed #125386",
                              boxShadow: "none",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "action.hover" },
                            }}
                          >
                            <CardContent
                              sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "#213360",
                                  width: 56,
                                  height: 56,
                                }}
                              >
                                P
                              </Avatar>
                              <div style={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                  No patient selected
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#6B7A90" }}
                                >
                                  Search by Patient ID / ABHA / Phone /
                                  Demographics
                                </Typography>
                              </div>
                            </CardContent>
                          </Card>
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenPatientSearch(true);
                            }}
                            style={{ textTransform: "none", paddingLeft: 0 }}
                          >
                            Search patient
                          </Button>
                        </>
                      );
                    }

                    const photoSrc = normalizeBase64Image(
                      selectedPatient.photo,
                    );
                    const gender = selectedPatient.gender || "-";
                    const dob = selectedPatient.dob || "-";
                    const age =
                      selectedPatient.age !== undefined &&
                      selectedPatient.age !== null
                        ? `${selectedPatient.age}`
                        : "-";
                    const phone =
                      selectedPatient.phone_no ||
                      selectedPatient.phone_number ||
                      "-";

                    return (
                      <>
                        <Card
                          onClick={() => setOpenPatientSearch(true)}
                          sx={{
                            borderRadius: "10px",
                            border: "1px solid #125386",
                            boxShadow: "none",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <CardContent
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                            }}
                          >
                            <Avatar
                              src={photoSrc || undefined}
                              alt={displayPatientName(
                                selectedPatient.name,
                                "Patient",
                              )}
                              sx={{ width: 56, height: 56, bgcolor: "#213360" }}
                            >
                              {String(selectedPatient?.name || "P")
                                .slice(0, 1)
                                .toUpperCase()}
                            </Avatar>
                            <div style={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                }}
                              >
                                {displayPatientName(selectedPatient.name, "-")}
                              </Typography>
                              <Typography variant="body2">
                                Patient ID: {selectedPatient.patient_id || "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                className="invoice-patient-meta"
                                component="div"
                              >
                                <span>Gender: {gender}</span>
                                <span>DOB: {dob}</span>
                                <span>Age: {age}</span>
                              </Typography>
                              <Typography variant="body2">
                                Phone: {phone}
                              </Typography>
                            </div>
                          </CardContent>
                        </Card>

                        <div
                          style={{ display: "flex", gap: 8 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => setOpenPatientSearch(true)}
                            style={{ textTransform: "none", paddingLeft: 0 }}
                          >
                            Change patient
                          </Button>
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => {
                              setPatientId("");
                              setPatientSearchResults([]);
                              setSelectedPatientSearchId("");
                              // setCareContextList([]);
                              // setSelectedCareContext("");
                            }}
                            style={{ textTransform: "none" }}
                          >
                            Clear
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </Grid>

                {/* Right Side (Other Fields) */}
                <Grid item xs={12} sm={6} md={6}>
                  <Grid container spacing={2}>
                    {/* Invoice Date */}
                    <Grid item xs={12} sm={6} md={6}>
                      <div className="input-wrapper">
                        <label htmlFor="invoiceDate" className="required-label">
                          Invoice Date{" "}
                        </label>
                        <div className="date-input-container">
                          <ApiDatePickerField
                            id="invoiceDate"
                            value={invoiceDate}
                            minDate={today}
                            maxDate="2050-01-01"
                            onChange={handleInvoiceDateChange}
                            slotProps={{
                              textField: {
                                required: true,
                                className: "form-control",
                                fullWidth: true,
                                onBlur: handleInvoiceDateBlur,
                              },
                            }}
                          />
                        </div>
                      </div>
                    </Grid>

                    {/* Expiry Date */}
                    <Grid item container xs={12} sm={6} md={6}>
                      <Grid item xs={12}>
                        <div className="input-wrapper">
                          <label htmlFor="dueDate" className="required-label">
                            Due Date{" "}
                          </label>
                          <div className="date-input-container">
                            <ApiDatePickerField
                              id="dueDate"
                              value={dueDate}
                              minDate={invoiceDate || today}
                              maxDate="2050-01-01"
                              disabled={fillExpiryDate}
                              onChange={handleDueDateChange}
                              slotProps={{
                                textField: {
                                  required: true,
                                  className: "form-control",
                                  fullWidth: true,
                                  onBlur: handleDueDateBlur,
                                },
                              }}
                            />
                          </div>
                        </div>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="fillExpiryDate"
                              name="fillExpiryDate"
                              checked={fillExpiryDate}
                              onChange={handleCheckboxChange}
                              disabled={!invoiceDate}
                            />
                          }
                          label="Same as Invoice Date"
                        />
                      </Grid>
                    </Grid>

                    {/* Visit Purpose / Care Context – hidden for now
                    {patientId && (
                      <Grid item xs={12} style={{ marginTop: 8 }}>
                        <div>
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() =>
                              setShowCareContextDropdown((prev) => !prev)
                            }
                            style={{
                              textTransform: "none",
                              paddingLeft: 0,
                              marginBottom: 4,
                            }}
                          >
                            {showCareContextDropdown ? "Hide" : "Show"} Visit
                            Purpose (Care Context)
                          </Button>
                          {showCareContextDropdown && (
                            <div style={{ marginTop: 4 }}>
                              <label
                                className="required-label"
                                style={{ display: "block", marginBottom: 4 }}
                              >
                                Visit Purpose
                              </label>
                              {isCareContextsLoading ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <CircularProgress size={20} />
                                  <span>Loading visit purposes…</span>
                                </div>
                              ) : (
                                <StyledTextFieldBootstrap
                                  fullWidth
                                  select
                                  value={selectedCareContext}
                                  onChange={(e) =>
                                    setSelectedCareContext(e.target.value)
                                  }
                                >
                                  <MenuItem value="">
                                    No visit purpose selected
                                  </MenuItem>
                                  {(careContextList || []).map((cc) => {
                                    const ref =
                                      cc.care_context_reference ||
                                      cc.care_context_id ||
                                      "";
                                    const display =
                                      cc.display || cc.visit_details || ref;
                                    return (
                                      <MenuItem key={ref} value={ref}>
                                        {display}
                                        {ref ? ` (${ref})` : ""}
                                      </MenuItem>
                                    );
                                  })}
                                </StyledTextFieldBootstrap>
                              )}
                            </div>
                          )}
                        </div>
                      </Grid>
                    )}
                    */}
                  </Grid>
                </Grid>
              </Grid>
            </Card>

            <Card
              className="invoice-form-card"
              sx={{ boxShadow: "0px 2px 4px rgba(0,0,0,0.05)" }}
            >
              {itemsList.map((item, index) => (
                <Grid
                  container
                  spacing={1.5}
                  key={index}
                  className="invoice-service-row"
                  alignItems="center"
                >
                  {/* Category Selection */}
                  <Grid item xs={12} sm={6} md={1}>
                    <RadioGroup
                      row
                      value={item.category || "services"}
                      onChange={(e) =>
                        handleItemChange(index, "category", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="services"
                        control={<Radio size="small" />}
                        label="Services"
                      />
                    </RadioGroup>
                  </Grid>

                  {/* Item Selection */}
                  <Grid item xs={12} sm={6} md={item.is_outsourced ? 3 : 4}>
                    {(() => {
                      const selectedId = item.items;
                      const selectedOption =
                        (servicesList || []).find(
                          (s) => s.medical_service_id === selectedId,
                        ) ||
                        (serviceMaster || []).find(
                          (s) => s.medical_service_id === selectedId,
                        ) ||
                        selectedServicesCacheRef.current.get(selectedId) ||
                        null;

                      const createNewOption = {
                        name: "+ Create New Service",
                        medical_service_id: "new",
                      };

                      const options = (() => {
                        const base = Array.isArray(servicesList)
                          ? servicesList
                          : [];
                        const shouldPrependSelected =
                          selectedOption &&
                          !base.some(
                            (s) =>
                              s.medical_service_id ===
                              selectedOption.medical_service_id,
                          );
                        return shouldPrependSelected
                          ? [selectedOption, ...base, createNewOption]
                          : [...base, createNewOption];
                      })();

                      return (
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={options}
                          value={selectedOption}
                          loading={isServicesLoading}
                          getOptionLabel={(option) => option?.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.medical_service_id ===
                            value?.medical_service_id
                          }
                          noOptionsText={
                            isServicesLoading ? "Loading..." : "No options"
                          }
                          onChange={(_, newValue) => {
                            if (!newValue) {
                              handleItemChange(index, "items", "");
                              setServiceSearchInputByRow((prev) => ({
                                ...prev,
                                [index]: "",
                              }));
                              return;
                            }

                            if (
                              newValue.medical_service_id === "new" ||
                              newValue.product_id === "new"
                            ) {
                              setActiveRowIndex(index);
                              setOpenCreateServiceModal(true);
                              return;
                            }

                            selectedServicesCacheRef.current.set(
                              newValue.medical_service_id,
                              newValue,
                            );
                            setServiceSearchInputByRow((prev) => ({
                              ...prev,
                              [index]: newValue.name || "",
                            }));
                            handleItemChange(
                              index,
                              "items",
                              newValue.medical_service_id,
                            );
                          }}
                          onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input" || reason === "clear") {
                              handleServiceSearchInputChange(
                                index,
                                newInputValue,
                              );
                            }
                          }}
                          inputValue={
                            serviceSearchInputByRow[index] ??
                            selectedOption?.name ??
                            ""
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Item"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "#ffffff",
                                  minHeight: "38px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#125386",
                                    borderWidth: "1.5px",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#125386",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#125386",
                                      borderWidth: "1.5px",
                                    },
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "16px",
                                  color: "#495057",
                                },
                                "& .MuiInputBase-input::placeholder": {
                                  color: "#6c757d",
                                  opacity: 1,
                                },
                                "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root":
                                  {
                                    color: "#6c757d",
                                  },
                              }}
                            />
                          )}
                        />
                      );
                    })()}
                  </Grid>

                  {/* Outsourced service: optional vendor; picking one bills the vendor's rate */}
                  {item.is_outsourced && (
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={item.vendor_id || ""}
                        onChange={(e) =>
                          handleItemChange(index, "vendor_id", e.target.value)
                        }
                        disabled={item.vendorsLoading}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            const vendor = (item.vendors || []).find(
                              (v) => String(v.vendor_id) === String(selected),
                            );
                            if (vendor) {
                              return `${vendor.vendor_name} — ₹${Number(vendor.rate).toFixed(2)}`;
                            }
                            return (
                              <span style={{ color: "#6c757d" }}>
                                {item.vendorsLoading
                                  ? "Loading vendors..."
                                  : (item.vendors || []).length === 0
                                    ? "No vendor mapped"
                                    : "No Outside Vendor Selected"}
                              </span>
                            );
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            minHeight: "38px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#125386",
                              borderWidth: "1.5px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#125386",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#125386",
                              borderWidth: "1.5px",
                            },
                          },
                          "& .MuiInputBase-input, & .MuiSelect-select": {
                            fontSize: "16px",
                            color: "#495057",
                          },
                          "& .MuiSelect-icon": { color: "#6c757d" },
                        }}
                      >
                        <MenuItem value="">No Outside Vendor Selected</MenuItem>
                        {(item.vendors || []).map((v) => (
                          <MenuItem key={v.vendor_id} value={v.vendor_id}>
                            {v.vendor_name} — ₹{Number(v.rate).toFixed(2)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {/* Doctor who ordered this line (required) */}
                  <Grid item xs={12} sm={6} md={1}>
                    <OrderedByDoctorSelect
                      doctors={facilityDoctors}
                      loading={facilityDoctorsLoading}
                      value={item.ordered_by_doctor_id || ""}
                      locked={!!item.ordered_by_locked}
                      customNames={referredByIds.filter(isCustomReferral)}
                      error={
                        doctorEngaged &&
                        !item.ordered_by_doctor_id &&
                        (showDoctorErrors || !!item.items)
                      }
                      onChange={(doctorId) =>
                        handleItemChange(index, "ordered_by_doctor_id", doctorId)
                      }
                    />
                  </Grid>

                  {/* Quantity Field */}
                  <Grid item xs={6} sm={4} md={1}>
                    <input
                      type="number"
                      className="form-control"
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #ced4da",
                      }}
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                      min="0"
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "+") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Grid>

                  {/* Price Field */}
                  <Grid item xs={6} sm={4} md={1}>
                    <input
                      type="text"
                      className="form-control"
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #ced4da",
                        backgroundColor: "#fff",
                      }}
                      value={item.price}
                      readOnly
                      placeholder="Price"
                    />
                  </Grid>

                  {/* Line Item Discount Field */}
                  <Grid item xs={6} sm={4} md={1}>
                    <input
                      type="number"
                      className="form-control"
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #ced4da",
                      }}
                      placeholder="Discount"
                      value={item.discount || ""}
                      onChange={(e) =>
                        handleItemChange(index, "discount", e.target.value)
                      }
                      min="0"
                      max="100"
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (
                          e.key === "-" ||
                          e.key === "+" ||
                          e.key === "e" ||
                          e.key === "E"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Grid>

                  {/* Amount Field */}
                  <Grid item xs={6} sm={4} md={item.is_outsourced ? 1 : 2}>
                    <input
                      type="text"
                      className="form-control"
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #ced4da",
                        backgroundColor: "#fff",
                      }}
                      value={item.amount}
                      readOnly
                      placeholder="Amount"
                    />
                  </Grid>

                  {/* Action Field */}
                  <Grid item xs={12} sm={12} md={1}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#bdbdbd",
                        color: "white",
                        minWidth: "80px",
                        width: { xs: "100%", md: "auto" },
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        "&:hover": { backgroundColor: "#9e9e9e" },
                      }}
                      onClick={() => handleRemoveItem(index)}
                    >
                      REMOVE
                    </Button>
                  </Grid>
                </Grid>
              ))}

              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ mt: 2 }}
              >
                <Grid item xs={12}>
                  <center>
                    <button
                      type="button"
                      className="btn btn__secondary btn__rounded btn__block invoice-action-btn"
                      onClick={handleAddItem}
                      style={{
                        color: "white",
                        padding: "8px 16px",
                        width: "25%",
                        backgroundColor: "#125386",
                        borderRadius: "10px",
                      }}
                    >
                      + ADD
                    </button>
                  </center>
                </Grid>
              </Grid>
            </Card>

            <Card className="invoice-form-card" sx={{ boxShadow: "none" }}>
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="center"
                style={{ outline: "none", border: "none", boxShadow: "none" }}
              >
                {/* Notes Section */}
                <Grid item xs={12} sm={6}>
                  <div style={{ maxWidth: "100%", flexGrow: 1 }}>
                    <textarea
                      id="notes"
                      name="notes"
                      placeholder="Notes"
                      value={notes}
                      onChange={(e) => handleNotesChange(e.target.value)}
                      style={{
                        width: "100%",
                        height: "330px",
                        borderRadius: "2%",
                        padding: "12px 16px",
                      }}
                      className="responsive-textarea"
                    />
                  </div>
                </Grid>

                {/* Pricing Section */}
                <Grid item xs={12} sm={6}>
                  {/* <div className="card" style={{ background: "cream", padding: "30px", borderRadius: "10px" }}> */}
                  {/* <div className="total-section"> */}
                  <Grid container>
                    <Grid item xs={12}>
                      {/* <div className="form-group">
                        <label htmlFor="partialPayment" style={{ color: "#808285" }}>Payment (Rs) {" "}</label>
                        <input
                          type="number"
                          className="form-control"
                          id="partialPayment"
                          name="partialPayment"
                          min="0"
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "+") {
                              e.preventDefault();
                            }
                          }}
                          style={{ width: "100%" }}
                        />
                      </div> */}
                    </Grid>

                    <Grid item xs={12}>
                      <div className="form-group">
                        <label
                          htmlFor="referredBy"
                          style={{ color: "#808285" }}
                        >
                          Referred By
                        </label>
                        <ReferredByDoctorField
                          value={referredByIds}
                          onChange={handleReferredByChange}
                          locked={referralLocked}
                          doctors={facilityDoctors}
                          loading={facilityDoctorsLoading}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={1} alignItems="flex-end">
                        <Grid item xs={4}>
                          <div
                            className="form-group"
                            style={{ marginBottom: 0 }}
                          >
                            <label
                              htmlFor="billDiscountAmount"
                              style={{ color: "#808285" }}
                            >
                              Discount (Rs)
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="billDiscountAmount"
                              name="billDiscountAmount"
                              value={billDiscountAmount}
                              min="0"
                              step="0.01"
                              onChange={(e) =>
                                handleTotalChange(
                                  "billDiscountAmount",
                                  e.target.value,
                                )
                              }
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => {
                                if (
                                  e.key === "-" ||
                                  e.key === "+" ||
                                  e.key === "e" ||
                                  e.key === "E"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={4}>
                          <div
                            className="form-group"
                            style={{ marginBottom: 0 }}
                          >
                            <label
                              htmlFor="billDiscount"
                              style={{ color: "#808285" }}
                            >
                              Discount (%)
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="billDiscount"
                              name="billDiscount"
                              value={billDiscount}
                              min="0"
                              max="100"
                              step="0.01"
                              onChange={(e) =>
                                handleTotalChange(
                                  "billDiscount",
                                  e.target.value,
                                )
                              }
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => {
                                if (
                                  e.key === "-" ||
                                  e.key === "+" ||
                                  e.key === "e" ||
                                  e.key === "E"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={4}>
                          <div
                            className="form-group"
                            style={{ marginBottom: 0 }}
                          >
                            <label
                              htmlFor="totalDiscount"
                              style={{ color: "#808285" }}
                            >
                              Total Discount (Rs)
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="totalDiscount"
                              value={getTotalDiscountAmount().toFixed(2)}
                              readOnly
                              title="Line item discounts + bill discount"
                              style={{
                                width: "100%",
                                backgroundColor: "#EEF2F6",
                              }}
                            />
                          </div>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <div className="form-group">
                        <label htmlFor="tax" style={{ color: "#808285" }}>
                          Add Tax (%){" "}
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="tax"
                          name="tax"
                          value={0}
                          readOnly
                          disabled
                          style={{ width: "100%" }}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12}>
                      <div className="form-group">
                        <label
                          htmlFor="totalAmount"
                          style={{ color: "#808285" }}
                        >
                          TOTAL AMOUNT(Rs)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="totalAmount"
                          name="totalAmount"
                          value={calculateTotalAmount()}
                          readOnly
                          style={{ width: "100%" }}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  {/* </div> */}
                  {/* </div> */}
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="center"
                style={{ marginTop: "5px", marginBottom: "5px" }}
              >
                <Grid item xs={6}>
                  <center>
                    <button
                      className="btn btn__secondary btn__rounded btn__block mt-2 invoice-submit-btn"
                      type="submit"
                      style={{
                        color: "white",
                        padding: "8px 16px",
                        width: "50%",
                        backgroundColor: "#0e3f66",
                        borderRadius: "10px",
                      }}
                    >
                      SUBMIT
                    </button>
                  </center>
                </Grid>
                <Grid item xs={6}>
                  <center>
                    <button
                      type="button"
                      onClick={handleGoBack}
                      className="btn btn__secondary btn__rounded btn__block mt-2 invoice-cancel-btn"
                      style={{
                        color: "white",
                        padding: "8px 16px",
                        width: "50%",
                        backgroundColor: "#0e3f66",
                        borderRadius: "10px",
                      }}
                    >
                      CANCEL
                    </button>
                  </center>
                </Grid>
              </Grid>

              {/* </div> */}
              {/* </div> */}
            </Card>
          </form>
          <InvoicePayment
            open={openPaymentModal}
            onClose={() => setOpenPaymentModal(false)}
            invoice={invoiceForPayment}
            onStatusUpdated={() => {}}
            onAfterClose={() => {}}
            disableClose={true}
            onSkip={() => {
              setOpenPaymentModal(false);
              history.push("/doctor/invoice");
            }}
            onPaymentSuccess={(paymentInfo) => {
              // Mark every linked doctor appointment as PAID (multi-doctor invoices).
              const appointmentIds = linkedAppointmentIdsRef.current?.length
                ? linkedAppointmentIdsRef.current
                : selectedAppointmentId
                  ? [selectedAppointmentId]
                  : [];

              appointmentIds.forEach((appointmentId) => {
                axios
                  .post(
                    `${REACT_APP_BACKEND_ROUTE_URL}change_payment_status/`,
                    {
                      appointment_id: appointmentId,
                      payment_status: "PAID",
                      payment_amount: paymentInfo?.paymentAmount,
                      payment_mode: paymentInfo?.paymentType,
                    },
                    { headers },
                  )
                  .then((res) => {
                    console.log(
                      "Appointment payment status updated:",
                      appointmentId,
                      res.data,
                    );
                  })
                  .catch((err) => {
                    console.error(
                      "Error updating appointment payment status:",
                      appointmentId,
                      err,
                    );
                  });
              });
              // Lab / Radiology tests on this invoice are moved to PAID by
              // payment_details/ itself.
            }}
          />
          <ServiceAdditionOnTheGo
            open={openCreateServiceModal}
            onClose={() => setOpenCreateServiceModal(false)}
            onCreated={handleServiceCreated}
          />

          <Dialog
            open={openAppointmentModal}
            onClose={dismissAppointmentPickerWithoutSelection}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle
              sx={{
                backgroundColor: "#213360",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Select Appointment</span>
              <IconButton onClick={dismissAppointmentPickerWithoutSelection}>
                <span style={{ color: "#FFFFFF", fontSize: 22 }}>×</span>
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div className="input-wrapper">
                    <label htmlFor="appointmentDate">Appointment Date</label>
                    <div
                      className="date-input-container"
                      style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="date"
                        className="form-control"
                        id="appointmentDate"
                        name="appointmentDate"
                        value={appointmentDate}
                        max="2050-01-01"
                        onChange={(e) => setAppointmentDate(e.target.value)}
                        style={{ paddingRight: "2.5rem" }}
                      />
                      <i
                        style={{
                          position: "absolute",
                          right: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                        }}
                      >
                        <CalendarIcon
                          onClick={() =>
                            document
                              .getElementById("appointmentDate")
                              .showPicker?.()
                          }
                        />
                      </i>
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <label htmlFor="appointmentSelect">Appointment</label>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={(appointmentList || []).filter((a) => {
                      if (!a?.appointment_id) return false;
                      if (
                        !appointmentMatchesSelectedService(
                          a,
                          appointmentPickerServiceId,
                        )
                      ) {
                        return false;
                      }
                      // Date is no longer sent to the API (all dates come back),
                      // so narrow to the selected appointment date client-side.
                      if (
                        appointmentDate &&
                        getApptDatePart(a.start_time_ist) !== appointmentDate
                      ) {
                        return false;
                      }
                      const usedIds = getUsedAppointmentIds(
                        appointmentPickerRowIndex,
                      );
                      return !usedIds.has(String(a.appointment_id));
                    })}
                    loading={isAppointmentsLoading}
                    value={
                      appointmentList.find(
                        (a) => a.appointment_id === selectedAppointmentId,
                      ) || null
                    }
                    getOptionLabel={(option) => getAppointmentLabel(option)}
                    isOptionEqualToValue={(option, value) =>
                      option?.appointment_id === value?.appointment_id
                    }
                    noOptionsText={
                      isAppointmentsLoading
                        ? "Loading..."
                        : "No unpaid appointment for this consultation fee on this date"
                    }
                    onChange={(_, newValue) =>
                      handleAppointmentSelect(newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select an appointment to fill patient"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={dismissAppointmentPickerWithoutSelection}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openPatientSearch}
            onClose={() => setOpenPatientSearch(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle
              sx={{
                backgroundColor: "#213360",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Search Patient</span>
              <IconButton onClick={() => setOpenPatientSearch(false)}>
                <span style={{ color: "#FFFFFF", fontSize: 22 }}>×</span>
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <label className="required-label">Search By</label>
                  <StyledTextFieldBootstrap
                    fullWidth
                    select
                    value={patientSearchType}
                    onChange={(e) => {
                      setPatientSearchType(e.target.value);
                      setPatientSearchResults([]);
                      setSelectedPatientSearchId("");
                    }}
                  >
                    <MenuItem value="patientId">Patient ID</MenuItem>
                    <MenuItem value="abhaId">ABHA Address</MenuItem>
                    <MenuItem value="abhaNo">ABHA No</MenuItem>
                    <MenuItem value="phoneNo">Phone No</MenuItem>
                    <MenuItem value="demographics">Demographics</MenuItem>
                  </StyledTextFieldBootstrap>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <label>Enter Search Query</label>
                  <StyledTextFieldBootstrap
                    fullWidth
                    placeholder="Enter your search query"
                    value={patientSearchQuery}
                    onChange={(e) => setPatientSearchQuery(e.target.value)}
                    disabled={patientSearchType === "demographics"}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <label>Name</label>
                  <StyledTextFieldBootstrap
                    fullWidth
                    placeholder="Enter patient name"
                    value={patientSearchName}
                    onChange={(e) => setPatientSearchName(e.target.value)}
                    disabled={patientSearchType !== "demographics"}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <label>Gender</label>
                  <StyledTextFieldBootstrap
                    fullWidth
                    select
                    value={patientSearchGender}
                    onChange={(e) => setPatientSearchGender(e.target.value)}
                    disabled={patientSearchType !== "demographics"}
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="M">Male</MenuItem>
                    <MenuItem value="F">Female</MenuItem>
                    <MenuItem value="O">Other</MenuItem>
                  </StyledTextFieldBootstrap>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <label>Date of Birth</label>
                  <StyledTextFieldBootstrap
                    fullWidth
                    type="date"
                    value={patientSearchDob}
                    onChange={(e) => setPatientSearchDob(e.target.value)}
                    disabled={patientSearchType !== "demographics"}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <div style={{ marginTop: 16 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Results
                </Typography>

                {isPatientSearching ? (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <CircularProgress size={20} />
                    <span>Searching…</span>
                  </div>
                ) : patientSearchResults && patientSearchResults.length > 0 ? (
                  <Grid container spacing={2}>
                    {patientSearchResults.map((p) => {
                      const isSelected =
                        selectedPatientSearchId === p.patient_id;
                      const phoneVal = p.phone_no || p.phone_number || "-";
                      return (
                        <Grid item xs={12} sm={6} key={p.patient_id}>
                          <Card
                            onClick={() => {
                              setSelectedPatientSearchId(p.patient_id);
                              selectPatientFromSearch(p);
                            }}
                            sx={{
                              cursor: "pointer",
                              border: isSelected
                                ? "2px solid #21cdc0"
                                : "1px solid #E3EFF0",
                              borderRadius: "10px",
                            }}
                          >
                            <CardContent sx={{ display: "flex", gap: 2 }}>
                              <Avatar
                                src={
                                  normalizeBase64Image(p?.photo) || undefined
                                }
                                alt={p?.name || "Patient"}
                                sx={{ bgcolor: "#213360" }}
                              >
                                {String(p?.name || "P")
                                  .slice(0, 1)
                                  .toUpperCase()}
                              </Avatar>
                              <div style={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                  {p?.name || "-"}
                                </Typography>
                                <Typography variant="body2">
                                  Patient ID: {p?.patient_id || "-"}
                                </Typography>
                                <Typography variant="body2">
                                  Phone: {phoneVal}
                                </Typography>
                                <Typography variant="body2">
                                  ABHA Number: {p?.abha_no || "-"}
                                </Typography>
                                <Typography variant="body2">
                                  ABHA Address: {p?.abha_id || "-"}
                                </Typography>
                              </div>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" sx={{ color: "#6B7A90" }}>
                    No results to show.
                  </Typography>
                )}
              </div>
            </DialogContent>

            <div
              style={{
                padding: "16px 24px",
                display: "flex",
                gap: "10px",
                width: "100%",
              }}
            >
              <button
                type="button"
                className="btn btn__secondary btn__block"
                onClick={() => setOpenPatientSearch(false)}
              >
                CLOSE
              </button>
              <button
                type="button"
                className="btn btn__secondary btn__block"
                onClick={runPatientSearch}
                disabled={isPatientSearching}
              >
                {isPatientSearching ? "SEARCHING..." : "SEARCH"}
              </button>
            </div>
          </Dialog>
        </div>
      </SideNavbar>
    </>
  );
};

export default InvoiceAddition;
